import {
  Popover,
  PopoverTrigger,
  PopoverContent,
  PopoverBody,
  Text,
  Flex,
  Icon,
  Box,
  Button,
  useDisclosure,
  useOutsideClick,
} from '@chakra-ui/react';
import { Clock } from '@/assets/icons/bold';
import { ChevronDownIcon } from '@heroicons/react/solid';
import Select from 'react-select';
import { customStyles } from '@/theme/components/select';
import { useRef, useState } from 'react';
import { timeOptions, periodOption } from '../constants/time';
import { to24Hours, to12Hours } from 'convert-string-time';
import { KText } from '@/ui-library/components/text';

const convertTime = (timeStr) => {
  if (!timeStr) return [9, 'AM'];
  const timeIn12Hrs = to12Hours(timeStr);
  const [timeInStr, period] = timeIn12Hrs?.split(' ');
  const [time] = timeInStr?.split(':');
  return [+time, period];
};

export const TimePicker = ({ day, disabled = false, onChange = () => {} }) => {
  const { isOpen, onOpen, onClose } = useDisclosure();
  const ref = useRef();
  const fromArr = convertTime(day?.start);
  const toArr = convertTime(day?.end);
  const [from, setFrom] = useState(() => {
    const time = fromArr[0];
    return timeOptions?.find(({ id }) => id === time);
  });
  const [fromPeriod, setFromPeriod] = useState(() => {
    const period = fromArr[1];
    return periodOption?.find(({ id }) => id === period);
  });
  const [to, setTo] = useState(() => {
    const time = toArr[0];
    return timeOptions?.find(({ id }) => id === time);
  });
  const [toPeriod, setToPeriod] = useState(() => {
    const period = toArr[1];
    return periodOption?.find(({ id }) => id === period);
  });
  const [error, setError] = useState(null);
  const [fromDisplay, setFromDisplay] = useState(from);
  const [fromPeriodDisplay, setFromPeriodDisplay] = useState(fromPeriod);
  const [toDisplay, setToDisplay] = useState(to);
  const [toPeriodDisplay, setToPeriodDisplay] = useState(toPeriod);

  const onSave = () => {
    const fromIn24Hrs = to24Hours(`${from?.label} ${fromPeriod?.id}`);
    const toIn24Hrs = to24Hours(`${to?.label} ${toPeriod?.id}`);
    const [fromTime] = fromIn24Hrs?.split(':');
    const [toTime] = toIn24Hrs?.split(':');

    if (+fromTime >= +toTime) {
      setError('Time interval invalid.');
      return;
    }
    setError(null);
    setFromDisplay(from);
    setFromPeriodDisplay(fromPeriod);
    setToDisplay(to);
    setToPeriodDisplay(toPeriod);
    onChange({ id: day?.id, start: fromIn24Hrs, end: toIn24Hrs });
    onClose();
  };

  useOutsideClick({
    ref: ref,
    handler: () => onClose(),
  });

  return (
    <Box ref={ref}>
      <Popover placement="top-end" isOpen={isOpen}>
        <PopoverTrigger>
          <Flex
            bg="secondary-50"
            borderRadius="8px"
            py="8px"
            px="12px"
            cursor="pointer"
            alignItems="center"
            minW="230px"
            onClick={onOpen}
          >
            {!disabled ? (
              <Flex flex="1" alignItems="center" justifyContent="center">
                <Box mr="8px" pos="relative">
                  <Icon as={Clock} color="secondary-700" />
                </Box>
                <KText color="secondary-900" size="sm-semi-bold" mr="8px">
                  <KText as="span" size="sm-semi-bold" color="secondary-900">
                    {fromDisplay?.label} {fromPeriodDisplay?.label}
                  </KText>
                  <KText
                    as="span"
                    size="sm-semi-bold"
                    color="secondary-900"
                    px="4px"
                  >
                    to
                  </KText>
                  <KText as="span" size="sm-semi-bold" color="secondary-900">
                    {toDisplay?.label} {toPeriodDisplay?.label}
                  </KText>
                </KText>
                <Icon as={ChevronDownIcon} color="secondary-700" />
              </Flex>
            ) : (
              <KText
                flex="1"
                size="sm-semi-bold"
                color="secondary-900"
                mr="8px"
                textAlign="center"
                variant="muted"
              >
                No Schedule
              </KText>
            )}
          </Flex>
        </PopoverTrigger>
        <PopoverContent width="420px">
          <PopoverBody>
            <Flex p="8px" flexDir="column">
              <Flex alignItems="center">
                <Flex>
                  <Box width="100px" mr="2px">
                    <Select
                      value={from}
                      options={timeOptions}
                      styles={customStyles}
                      backspaceRemovesValue={false}
                      controlBorderRadius="6px 0 0 6px"
                      components={{
                        IndicatorSeparator: null,
                      }}
                      onChange={setFrom}
                      getOptionLabel={(option) => option?.label}
                      getOptionValue={(option) => option?.id}
                    />
                  </Box>
                  <Box width="70px">
                    <Select
                      value={fromPeriod}
                      options={periodOption}
                      styles={customStyles}
                      backspaceRemovesValue={false}
                      controlBorderRadius="0 6px 6px 0"
                      components={{
                        IndicatorSeparator: null,
                        NoOptionsMessage: null,
                      }}
                      onChange={setFromPeriod}
                      getOptionLabel={(option) => option?.label}
                      getOptionValue={(option) => option?.id}
                    />
                  </Box>
                </Flex>
                <KText size="sm-semi-bold" color="secondary-900" mx="4px">
                  To
                </KText>
                <Flex>
                  <Box width="100px" mr="2px">
                    <Select
                      value={to}
                      options={timeOptions}
                      styles={customStyles}
                      backspaceRemovesValue={false}
                      controlBorderRadius="6px 0 0 6px"
                      components={{
                        IndicatorSeparator: null,
                      }}
                      onChange={setTo}
                      getOptionLabel={(option) => option?.label}
                      getOptionValue={(option) => option?.id}
                    />
                  </Box>
                  <Box width="70px">
                    <Select
                      value={toPeriod}
                      options={periodOption}
                      styles={customStyles}
                      backspaceRemovesValue={false}
                      controlBorderRadius="0 6px 6px 0"
                      components={{
                        IndicatorSeparator: null,
                        NoOptionsMessage: null,
                      }}
                      onChange={setToPeriod}
                      getOptionLabel={(option) => option?.id}
                      getOptionValue={(option) => option?.label}
                    />
                  </Box>
                </Flex>
              </Flex>
              {error && (
                <Text mt="12px" color="#E53E3E">
                  {error}
                </Text>
              )}
              <Flex mt="12px" justifyContent="right">
                <Button variant="secondary" mr="8px" onClick={onClose}>
                  Cancel
                </Button>
                <Button onClick={onSave}>Submit</Button>
              </Flex>
            </Flex>
          </PopoverBody>
        </PopoverContent>
      </Popover>
    </Box>
  );
};
